import React from 'react';
import { AccountProps, PurchaseHistoryItemProps } from '../../state/context';
import { getLocalDate, getSalutation } from '../../utils/string';
import { ConfirmationLogoZPP, SignatureMarife } from '../../assets';
import { Document, Font, Image, Page, Path, PDFDownloadLink, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';

interface ItemProps {
    item: PurchaseHistoryItemProps;
    account: AccountProps;
    isFreebie: boolean;
}

const ParticipationConfirmationItem: React.FC<ItemProps> = ({ item, account, isFreebie }) => {
    return (
        <div className="account-item">
            <div>
                Du hast <b>{item.title}</b> am {getLocalDate(item.endDate)} absolviert.
            </div>
            <PDFDownloadLink
                document={
                    isFreebie ? (
                        <PrintLayoutSimple item={item} account={account} />
                    ) : (
                        <PrintLayoutExtended item={item} account={account} />
                    )
                }
                fileName="gymondo-prevention.pdf"
            >
                {({ loading, error }) => {
                    if (loading) {
                        return <div className="link link-no-icon">Lädt...</div>;
                    }
                    if (error) {
                        return <div className="link link-no-icon">Fehlgeschlagen</div>;
                    }
                    return <div className="link">Herunterladen</div>;
                }}
            </PDFDownloadLink>
        </div>
    );
};

Font.register({
    family: 'Guillon',
    fonts: [
        {
            src: '//d11y8h2xe0qeoe.cloudfront.net/fonts/guillon-light/guillon-light.ttf',
        },
        {
            src: '//d11y8h2xe0qeoe.cloudfront.net/fonts/guillon-demi/guillon-demi.ttf',
            fontWeight: 'bold',
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        padding: '20',
        fontFamily: 'Guillon',
        fontWeight: 'normal',
        color: '#999',
    },
    simpleLayout: {
        fontSize: 12,
    },
    extendedLayout: {
        fontSize: 8,
    },
    logo: {
        width: 90,
    },
    section: {
        marginBottom: 25,
    },
    boldText: {
        fontWeight: 'bold',
    },
    extendedLayoutHeader: {
        display: 'flex',
        flexDirection: 'row',
        textAlign: 'left',
    },
    logoZPP: {
        width: 70,
    },
    extendedLayoutTitle: {
        width: 400,
        fontSize: 12,
        margin: '30 80 40 0',
    },
    textBox: {
        lineHeight: 2,
        margin: '10 0',
    },
    firstTextSection: {
        width: 350,
    },
    stamp: {
        marginLeft: 30,
        height: 150,
        width: 170,
        padding: 4,
        textAlign: 'center',
        position: 'relative',
    },
    stampBorder: {
        height: 50,
        width: 50,
        position: 'absolute',
        borderColor: 'green',
    },
    borderLeft: {
        borderLeftWidth: 2,
        borderTopWidth: 2,
        // borderColor: 'green',
    },
    borderRight: {
        borderRightWidth: 2,
        borderBottomWidth: 2,
        right: 0,
        bottom: 0,
        // borderColor: 'green',
    },
    head: {
        padding: '10 0 3',
    },
    list: {
        marginLeft: 15,
    },
    signature: {
        width: 120,
        height: 60,
        position: 'absolute',
        right: 100,
        bottom: -45,
    },
    signatureParam: {
        width: 50,
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    tableRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    borderLine: {
        borderTop: 1,
    },
    itemName: {
        fontSize: 8,
        padding: '2 0 10',
    },
    itemValue: {
        color: 'black',
        paddingTop: 5,
        marginRight: 2,
    },
    checkBoxWrapper: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    checkItem: {
        width: 150,
        margin: '10 20 0 0',
        display: 'flex',
        flexDirection: 'row',
    },
    checkBox: {
        height: 7,
        width: 7,
        border: 0.5,
        marginTop: 2,
    },
    checkText: {
        paddingLeft: 2,
    },
    checked: {
        height: 7,
        width: 7,
    },
});

const PrintLayoutSimple: React.FC<Omit<ItemProps, 'isFreebie'>> = ({ item, account }) => {
    return (
        <Document>
            <Page size="A4" style={[styles.page, styles.simpleLayout]}>
                <View style={{ marginBottom: 15 }}>
                    <Svg viewBox="0 0 76.562 19.968" style={styles.logo}>
                        <Path
                            d="M31.06 13.9c-2.338 0-4.012-1.7-4.012-4.122s1.768-4.099 4.045-4.099c1.316 0 2.403.595 3.105 1.555l-1.005.96a2.52 2.52 0 0 0-2.122-1.162c-1.388 0-2.473 1.102-2.473 2.746s1.05 2.79 2.495 2.79c.92 0 1.507-.28 1.887-.592v-1.22h-1.916V9.445h3.316v3.093c-.702.812-2.013 1.361-3.32 1.361zm16.915-5.758c-.588 0-1.275.266-1.575.888-.277-.555-.939-.888-1.594-.888-.655 0-1.23.322-1.575.788v-.622h-1.333v5.424h1.4v-2.96c0-.833.397-1.309 1.052-1.309.611 0 .962.431.962 1.142v3.127h1.4V10.65c0-.69.397-1.187 1.052-1.187.589 0 .962.431.962 1.142v3.127h1.4V10.33c0-1.51-1.162-2.188-2.15-2.188zm5.995 0c-1.67 0-2.968 1.186-2.968 2.873 0 1.714 1.31 2.884 2.967 2.884 1.648 0 2.967-1.17 2.967-2.884 0-1.7-1.319-2.873-2.967-2.873zm0 4.569c-.805 0-1.546-.676-1.546-1.696 0-.993.728-1.685 1.545-1.685.823 0 1.546.667 1.546 1.685 0 1.02-.743 1.696-1.546 1.696zm19.625-4.569c-1.67 0-2.967 1.186-2.967 2.873 0 1.714 1.31 2.884 2.967 2.884 1.648 0 2.967-1.17 2.967-2.884 0-1.7-1.32-2.873-2.967-2.873zm0 4.569c-.804 0-1.545-.676-1.545-1.696 0-.993.728-1.685 1.545-1.685.822 0 1.545.667 1.545 1.685 0 1.02-.742 1.696-1.545 1.696zm-12.71-4.569c-.789 0-1.44.392-1.683.913v-.747h-1.333v5.424h1.4v-2.907c0-.89.506-1.395 1.216-1.395.667 0 1.126.453 1.126 1.264v3.038h1.4V10.33c0-1.478-1.059-2.188-2.126-2.188zm7.445.666c-.368-.414-.996-.666-1.684-.666-1.507 0-2.728 1.14-2.728 2.873 0 1.755 1.208 2.884 2.694 2.884.778 0 1.379-.259 1.806-.777v.61h1.31v-7.7H68.33zM66.878 12.7c-.807 0-1.54-.674-1.54-1.684 0-1.006.752-1.651 1.54-1.651.822 0 1.55.674 1.55 1.65 0 1.04-.728 1.685-1.55 1.685zm-26.26.498c0 1.734-1.286 2.834-2.906 2.834-.72 0-1.61-.197-2.216-.595l.605-1.128c.446.273 1.003.47 1.519.47.951 0 1.696-.55 1.696-1.54v-.358c-.293.57-.98.851-1.683.851-1.134 0-2.126-.677-2.126-2.188V8.32h1.4v2.891c0 .811.395 1.233 1.094 1.233.673 0 1.217-.438 1.217-1.426V8.32h1.4v4.94m-26.631 6.708c-1.2 0-1.963-.218-2.828-.581-.865.368-1.594.581-2.743.581C3.662 19.968 0 15.59 0 9.985S3.647 0 8.304 0c1.09 0 2.112.28 2.738.594A6.779 6.779 0 0 1 13.71 0c3.134 0 5.988 2.116 5.988 4.451 0 1.314-.991 2.673-2.65 2.673-1.774 0-2.747-1.339-3.686-2.634-.668-.921-1.35-1.934-2.355-2.552C8.582 3.272 6.88 6.417 6.88 9.985c0 3.644 1.734 6.75 4.263 8.055 2.13-1.115 3.743-3.525 3.743-6.107-.655.714-1.469 1.39-2.685 1.39-1.863 0-2.976-1.485-2.976-2.92 0-1.655 1.347-2.952 3.067-2.952 1.096 0 2.345.633 3.172 2.014.65-.67 1.598-1.245 2.642-1.245 2.474 0 3.324 2.208 3.324 3.599 0 4.347-3.445 8.15-7.443 8.15zm-1.303-1.396a8.43 8.43 0 0 0 1.242.126c3.298 0 6.192-3.231 6.192-6.88 0-.635-.321-2.296-2.012-2.296-.856 0-1.51.52-2.127 1.195.119.344.175.845.175 1.303 0 2.646-1.339 5.086-3.47 6.552zm-4.38-17.3c-3.92 0-6.992 3.843-6.992 8.713 0 4.787 3.156 8.713 7.238 8.713a5.36 5.36 0 0 0 1.08-.122c-2.416-1.734-4.061-4.91-4.061-8.591 0-3.63 1.636-6.792 4-8.543a4.712 4.712 0 0 0-1.265-.17zm4.008 7.491c-1 0-1.775.705-1.775 1.64 0 .622.472 1.608 1.684 1.608.946 0 1.693-.812 2.364-1.582-.49-1.045-1.525-1.666-2.273-1.666zm.098-7.295c.813.688 1.47 1.501 2.015 2.251.941 1.298 1.572 2.094 2.624 2.094.975 0 1.338-.836 1.338-1.362 0-1.426-2.086-3.18-4.676-3.18-.427 0-.888.088-1.301.197z"
                            fill="black"
                        />
                    </Svg>
                </View>
                <View style={styles.section}>
                    <Text>Datum {getLocalDate()}</Text>
                </View>
                <View>
                    <Text style={[styles.section, styles.boldText]}>Teilnahmebestätigung</Text>
                </View>
                <View style={styles.section}>
                    <Text>
                        {getSalutation(account.gender)} {account.firstName} {account.lastName}, geboren am{' '}
                        {getLocalDate(account.birthDate)}
                    </Text>
                </View>
                {account.healthInsurance && (
                    <View style={styles.section}>
                        <Text>Krankenversichertennummer {account.healthInsurance}</Text>
                    </View>
                )}
                <View style={styles.section}>
                    <Text>
                        hat vom {getLocalDate(item.startDate)} bis zum {getLocalDate(item.endDate)}&nbsp; am
                        Online-Präventionskurs <Text style={styles.boldText}>{item.title}</Text> teilgenommen.
                    </Text>
                </View>
                <View style={styles.section}>
                    <Text>
                        Dieser Kurs ist unter der ID {item.courseId} nach § 20 SGB V von der Zentralen Prüfstelle
                        Prävention für das Handlungsfeld &quot;Bewegung&quot; zertifiziert.
                    </Text>
                </View>
                <View style={styles.section}>
                    <Text>Angaben zum Anbieter:</Text>
                    <Text>Gymondo GmbH</Text>
                    <Text>Ritterstraße 12-14, 10969 Berlin</Text>
                    <Text>service@gymondo.de</Text>
                </View>
                <View style={styles.section}>
                    <Text>Diese Bestätigung wurde maschinell erstellt und ist ohne Unterschrift gültig.*</Text>
                </View>
                <View style={styles.section}>
                    <Text>
                        *Diese Bestätigung berechtigt nicht zur Beantragung eines Zuschusses zur Teilnahmegebühr bei der
                        zuständigen Krankenkasse, sondern dient lediglich dazu, mögliche Bonusvorteile der Krankenkasse
                        zu nutzen.
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

const PrintLayoutExtended: React.FC<Omit<ItemProps, 'isFreebie'>> = ({ item, account }) => {
    return (
        <Document>
            <Page size="A4" style={[styles.page, styles.extendedLayout]}>
                <View style={styles.extendedLayoutHeader}>
                    <Text style={[styles.extendedLayoutTitle, styles.boldText]}>
                        Antrag auf Bezuschussung des Versicherten, Teilnahmebescheinigung und Verpflichtungserklärung
                        des Anbieters Individuelle Massnahmen (Kurse, Seminare) der Primärprävention nach § 20 SGB V
                    </Text>
                    <View>
                        <Image src={ConfirmationLogoZPP} style={styles.logoZPP} />
                    </View>
                </View>
                <View style={styles.extendedLayoutHeader}>
                    <View style={[styles.textBox, styles.firstTextSection]}>
                        <Text>
                            <Text style={styles.boldText}>Der Versicherte </Text>
                            füllt Teil 1 „Antrag auf Bezuschussung“ dieses Formulars aus.
                        </Text>
                        <Text>
                            <Text style={styles.boldText}>Der Anbieter </Text>
                            füllt Teil 2 „Teilnahmebescheinigung und Verpflichtungserklärung“ aus.
                        </Text>
                        <Text>
                            <Text style={styles.boldText}>Der Versicherte </Text>
                            leitet das ausgefüllte Formular an die Krankenkasse weiter.
                        </Text>
                        <View>
                            <Text>
                                <Text style={styles.boldText}>Datenschutzhinweis für den Versicherten: </Text>
                                Die Krankenkasse verwendet die hier erhobenen Daten um zu prüfen, ob sie Kosten
                                erstatten kann (§ 284 Abs. 1 Nr. 4 SGB V). Die Daten werden zum Zweck des Nachweises der
                                Teilnahme und der überweisung des Erstattungsbetrages erhoben, verarbeitet und genutzt.
                                Die Angabe der Daten ist freiwillig. Ohne die Angaben kann eine überweisung nicht
                                vorgenommen werden (§§ 67 a, b SGB X).
                            </Text>
                        </View>
                    </View>
                    <View style={styles.stamp}>
                        <View style={[styles.stampBorder, styles.borderLeft]} />
                        <Text>Vermerke der Krankenkasse:</Text>
                        <View style={[styles.stampBorder, styles.borderRight]} />
                    </View>
                </View>
                <View>
                    <Text style={styles.head}>
                        <Text style={styles.boldText}>1. Antrag auf Bezuschussung </Text>
                        (vom Versicherten bzw. dessen gesetzlichem Vertreter in DRUCKBUCHSTABEN auszufüllen)
                    </Text>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '25%' }]}>{account.lastName}</Text>
                        <Text style={[styles.itemValue, { width: '25%' }]}>{account.firstName}</Text>
                        <Text style={[styles.itemValue, { width: '25%' }]}>{getLocalDate(account.birthDate)}</Text>
                        <Text style={[styles.itemValue, { width: '25%' }]}>{account.healthInsurance}</Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '25%' }]}>Name des Versicherten</Text>
                        <Text style={[styles.itemName, { width: '25%' }]}>Vorname</Text>
                        <Text style={[styles.itemName, { width: '25%' }]}>Geburtsdatum</Text>
                        <Text style={[styles.itemName, { width: '25%' }]}>
                            Versicherten - Nr. (s. Krankenversichertenkarte)
                        </Text>
                    </View>
                </View>
                <View style={styles.head}>
                    <Text style={styles.boldText}>Ich beantrage einen Zuschuss zu der Maßnahme</Text>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.itemValue}>
                        <Text style={styles.itemValue}>{`${item.title}; KursID: ${item.courseId}`}</Text>
                    </View>
                    <Text style={[styles.itemName, styles.borderLine]}>Titel</Text>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.itemValue}>
                        <Text>Gymondo GmbH, Ritterstraße 12-14, 10969 Berlin</Text>
                    </View>
                    <Text style={[styles.itemName, styles.borderLine]}>Name und Adresse des Anbieters</Text>
                </View>
                <View style={styles.textBox}>
                    <Text>
                        Ich bestätige, dass ich an der o. g. Maßnahme teilgenommen habe und dass sie vom umseitig
                        genannten Anbieter/Kursleiter persönlich durchgeführt wurde. Ich füge einen Nachweis (z. B.
                        Original-Quittung oder Kontoauszug) darüber bei, dass ich die Teilnahmegebühr gezahlt habe.
                    </Text>
                    <Text>
                        <Text style={styles.boldText}>
                            Bei Angeboten von Einrichtungen, die auch Mitgliedsbeiträge erheben:&nbsp;
                        </Text>
                        Ich bestätige, dass der Zuschuss der Krankenkasse nicht mit aktuellen, früheren oder zukünftigen
                        Mitgliedsbeiträgen verrechnet wird. Die Teilnahmegebühr wird mir nicht als Geld- oder
                        Sachleistung erstattet. Die Teilnahme am Kurs ist nicht an die Bedingung einer derzeitigen oder
                        zukünftigen Mitgliedschaft geknüpft. Ein zu Unrecht erhaltener Zuschuss ist zurückzuzahlen.
                    </Text>
                    <Text style={styles.boldText}>Ich bitte um überweisung auf mein Konto:</Text>
                </View>
                <View>
                    <View style={[styles.itemValue, styles.borderLine]} />
                    <Text style={styles.itemName}>Kontoinhaber</Text>
                </View>
                <View style={styles.tableContainer}>
                    <Text style={{ paddingTop: 10 }} />
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '30%' }]}>IBAN</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>BIC</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>Bank</Text>
                    </View>
                </View>
                <View style={styles.tableContainer}>
                    <Text style={{ paddingTop: 10 }} />
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '30%' }]}>Ort</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>Datum</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>Unterschrift des Versicherten</Text>
                    </View>
                </View>
                <View>
                    <Text style={styles.head}>
                        <Text style={styles.boldText}>2. Teilnahmebescheinigung und Verpflichtungserklärung&nbsp;</Text>
                        (vom Anbieter in DRUCKBUCHSTABEN auszufüllen)
                    </Text>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={{ width: '60%' }}>
                            <Text style={styles.itemValue}>{`${getSalutation(account.gender)} ${account.firstName} ${
                                account.lastName
                            }`}</Text>
                        </Text>
                        <Text style={{ paddingTop: 3 }}>&nbsp;hat an der umseitig genannten Maßnahme</Text>
                    </View>
                    <View style={[styles.borderLine, styles.tableRow, { width: '60%' }]}>
                        <Text style={styles.itemName}>Herr/Frau</Text>
                    </View>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '50%' }]}>{getLocalDate(item.startDate)}</Text>
                        <Text style={[styles.itemValue, { width: '50%' }]}>{getLocalDate(item.endDate)}</Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '50%' }]}>vom (Tag, Monat, Jahr)</Text>
                        <Text style={[styles.itemName, { width: '50%' }]}>bis zum (Tag, Monat, Jahr)</Text>
                    </View>
                </View>

                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <View style={[styles.tableRow, { width: '60%' }]}>
                            <Text style={[styles.itemValue, { width: '20%' }]}>{item.lastDoneUnit}</Text>
                            <Text style={[styles.itemValue, { width: '20%' }]}>{item.totalWorkouts}</Text>
                            <Text style={[styles.itemValue, { width: '20%' }]}>{'50'}</Text>
                        </View>
                        <Text style={{ marginLeft: 10 }}> Minuten Dauer teilgenommen</Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine, { width: '60%' }]}>
                        <Text style={[styles.itemName, { width: '20%' }]}>an</Text>
                        <Text style={[styles.itemName, { width: '20%' }]}>von</Text>
                        <Text style={[styles.itemName, { width: '20%' }]}>Kurseinheit</Text>
                    </View>
                </View>

                <View>
                    <Text>
                        Präventionsprinzip/Inhalt: (nur ein Präventionsprinzip ankreuzen, dasjenige, auf dem der
                        Schwerpunkt liegt)
                    </Text>
                </View>

                <View style={[styles.textBox, styles.checkBoxWrapper]}>
                    <View style={styles.checkItem}>
                        <Svg viewBox="0 0 500 500" style={styles.checkBox}>
                            <Path
                                d="M55.7 15.2L13.2 57.6l195 195-195 194.9 42.5 42.4L250 295.6l194.3 194.3 42.5-42.4L293 252.6l193.8-195-42.5-42.4L250 209.5z"
                                fill="gray"
                            />
                        </Svg>
                        <Text style={styles.checkText}>
                            Reduzierung von Bewegungsmangel durch gesundheitssportliche Aktivitäten
                        </Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}> Förderung von Stressbewältigungskompetenzen</Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}> Vermeidung von Mangel-/Fehlernährung</Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}> Vermeidung und Reduktion von übergewicht</Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}>
                            Vorbeugung/Reduzierung spezieller gesundheitlicher Risiken durch geeignete
                            Bewegungsprogramme
                        </Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}> Förderung von Entspannung</Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}>
                            Gesundheitsgerechter Umgang mit Alkohol/Reduzierung des Alkoholkonsums
                        </Text>
                    </View>
                    <View style={styles.checkItem}>
                        <Text style={styles.checkBox} />
                        <Text style={styles.checkText}> Förderung des Nichtrauchens</Text>
                    </View>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '50%' }]}>{item.trainer.name}</Text>
                        <Text style={[styles.itemValue, { width: '50%' }]}>
                            {item.price.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                            })}
                        </Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '50%' }]}>Kursleiter</Text>
                        <Text style={[styles.itemName, { width: '50%' }]}>Teilnahmegebühr in Euro</Text>
                    </View>
                </View>

                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '30%' }]}>&nbsp;</Text>
                        <Text style={[styles.itemValue, { width: '40%' }]}>{item.trainer.qualification}</Text>
                        <Text style={[styles.itemValue, { width: '30%' }]}>&nbsp;</Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '30%' }]}>Ggf. weitere Kursleiter (Name)</Text>
                        <Text style={[styles.itemName, { width: '40%' }]}>
                            Qualifikation (staatl. anerkannter Berufs-/Studienabschluss)
                        </Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>
                            Zusatzqualifikation (nur Maßnahmebezogene Qualifikation)
                        </Text>
                    </View>
                </View>
                <View style={styles.textBox}>
                    <Text>
                        Der o. g. Kursleiter hat/die o. g. Kursleiter haben die Maßnahme persönlich durchgeführt. Die
                        Teilnehmerin/der Teilnehmer hat die o. g. Gebühr entrichtet.
                    </Text>
                    <Text>
                        Die Maßnahme wurde von der Krankenkasse als förderfähig anerkannt und nach den
                        Qualitätskriterien des GKV-Leitfadens Prävention in der geltenden Fassung umgesetzt.
                    </Text>
                    <Text>
                        Ich versichere, der Steuerpflicht aus den vorgenannten Einnahmen nachzukommen und den
                        Verpflichtungen zur Abführung von Beiträgen zur Sozialversicherung zu entsprechen.
                    </Text>
                    <Text>
                        <Text style={styles.boldText}>
                            Bei Angeboten von Einrichtungen, die auch Mitgliedsbeiträge erheben:&nbsp;
                        </Text>
                        <Text>
                            Ich bestätige, dass der Zuschuss der Krankenkasse nicht mit aktuellen, früheren oder
                            zukünftigen Mitgliedsbeiträgen verrechnet wird. Die Teilnahmegebühr wird dem Teilnehmer/der
                            Teilnehmerin nicht als Geld- oder Sachleistung erstattet. Die Teilnahme am Kurs ist nicht an
                            die Bedingung einer derzeitigen oder zukünftigen Mitgliedschaft geknüpft.
                        </Text>
                    </Text>
                    <Text>
                        Bei wohnortfernen Angeboten: Ich versichere, dass die Kursgebühr ausschließlich der Bezahlung
                        des genannten Präventionsangebotes dient und es keinerlei Quersubventionierung von
                        übernachtungs-, Verpflegungs- oder sonstigen Kosten gibt.
                    </Text>
                    <Text style={styles.boldText}>
                        Ich versichere, dass die hier gemachten Angaben der Wahrheit entsprechen.
                    </Text>
                    <Text>
                        Die Krankenkasse hat das Recht, die Einhaltung der Kriterien des GKV-Leitfadens Prävention in
                        der geltenden Fassung auch vor Ort unangemeldet zu überprüfen.
                    </Text>
                    <Text style={styles.boldText}>
                        Ich nehme mögliche rechtliche Folgen von Verstößen gegen den GKV-Leitfaden Prävention zur
                        Kenntnis:
                    </Text>
                    <Text>
                        Sofern ich als Anbieter die mir nach dem GKV-Leitfaden Prävention obliegenden Pflichten nicht
                        erfülle und/oder entgegen dessen Bestimmungen handle, kann von der betroffenen Krankenkasse
                        Abhilfe und/oder Unterlassung verlangt werden. Hierfür setzt die Krankenkasse eine angemessene
                        Frist. Bei schwerwiegenden oder wiederholten Verstößen kann die betroffene Krankenkasse nach
                        erfolgter Anhörung eine angemessene Strafzahlung bis zu 5.000 EURO festsetzen. Unabhängig davon
                        ist der entstandene Schaden zu ersetzen. Ich verpflichte mich, den Versicherten insoweit
                        freizustellen und zu Unrecht erhaltene Beträge direkt an die betroffene Krankenkasse
                        zurückzuführen.
                    </Text>
                    <Text>
                        Schwerwiegende oder wiederholte Verstöße rechtfertigen ferner den Ausschluss von weiterer
                        Förderung der von mir angebotenen Maßnahmen. Schwerwiegende Verstöße gegen den GKV-Leitfaden
                        Prävention sind insbesondere:
                    </Text>
                </View>
                <View style={[styles.textBox, styles.list]}>
                    <Text>
                        <Text>
                            • Nichterfüllung organisatorischer, sächlicher, fachlicher und/oder personeller
                            Voraussetzungen;
                        </Text>
                    </Text>
                    <Text>
                        <Text>• Abrechnung nicht erbrachter Leistungen;</Text>
                    </Text>
                    <Text>
                        <Text>• Nicht fristgerechte Beseitigung von Beanstandungen.</Text>
                    </Text>
                </View>
                <View style={styles.head}>
                    <Text style={styles.boldText}>Angaben zum Anbieter</Text>
                </View>

                <View style={styles.tableContainer}>
                    <View style={styles.itemValue}>
                        <Text>Ritterstraße 12-14, 10969 Berlin</Text>
                    </View>
                    <Text style={[styles.itemName, styles.borderLine]}>Adress</Text>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '50%' }]}>+49 30 364 280 040</Text>
                        <Text style={[styles.itemValue, { width: '50%' }]}>service@gymondo.de</Text>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '50%' }]}>Telefonnummer</Text>
                        <Text style={[styles.itemName, { width: '50%' }]}>E-Mail Adresse</Text>
                    </View>
                </View>
                <View style={styles.tableContainer}>
                    <View style={styles.tableRow}>
                        <Text style={[styles.itemValue, { width: '30%' }]}>Berlin</Text>
                        <Text style={[styles.itemValue, { width: '30%' }]}>{getLocalDate()}</Text>
                        <View style={styles.signature}>
                            <Image src={SignatureMarife} />
                        </View>
                    </View>
                    <View style={[styles.tableRow, styles.borderLine]}>
                        <Text style={[styles.itemName, { width: '30%' }]}>Ort</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>Datum</Text>
                        <Text style={[styles.itemName, { width: '30%' }]}>Persönliche Unterschrift des Anbieters</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default ParticipationConfirmationItem;
